import type { ActionResult, SlidersFeatureConfig, TransitionProfile, WidgetState } from '@examdojo/core/widget';
import {
  DEFAULT_DISPLAY_CONFIG,
  DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG,
  DEFAULT_JSXGRAPH_FEATURES,
  JsxgraphBaseWidget,
  JsxgraphStatic,
  JsxgraphThemeService,
} from '@examdojo/core/widget';
import type { GeometryElement } from 'jsxgraph';
import katex from 'katex';
import { ARITHMETIC_SERIES_METADATA } from './arithmetic-series.metadata';
import {
  ArithmeticSeriesActionType,
  ArithmeticSeriesBehaviorConfig,
  ArithmeticSeriesDisplayConfig,
  ArithmeticSeriesFeatureConfig,
  ArithmeticSeriesWidgetConfig,
  DEFAULT_ARITHMETIC_SERIES_PARAMS,
  DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS,
} from './arithmetic-series.model';

/**
 * Widget for visualizing arithmetic series Sₙ = u₁ + u₂ + ... + uₙ
 * Shows cumulative sum as a step line with hover interaction
 */
export class ArithmeticSeriesWidget extends JsxgraphBaseWidget<ArithmeticSeriesWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `arithmetic-series-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  static readonly metadata = ARITHMETIC_SERIES_METADATA;

  readonly id: string;
  readonly type = 'arithmetic-series';
  private config?: ArithmeticSeriesWidgetConfig;
  private sliders: Map<string, GeometryElement> = new Map();
  private params = { a: 2, d: 3, numTerms: 7 };
  private hoveredTerm: number | null = null;
  private tooltipElement: GeometryElement | null = null;

  protected getCurrentConfig(): ArithmeticSeriesWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    const config = this.config;
    if (!config) {
      return { type: this.type };
    }

    return {
      type: this.type,
      id: this.id,
      params: {
        ...config.params,
        a: this.getParam('a'),
        d: this.getParam('d'),
        numTerms: this.getParam('numTerms'),
      },
      display: config.display,
      behavior: config.behavior,
    };
  }

  init(config: ArithmeticSeriesWidgetConfig): void {
    this.sliders.clear();
    this.hoveredTerm = null;

    const params = { ...DEFAULT_ARITHMETIC_SERIES_PARAMS, ...(config.params || {}) };

    const display = {
      ...DEFAULT_DISPLAY_CONFIG,
      ...(config.display || {}),
    } as ArithmeticSeriesDisplayConfig;

    const features = {
      ...DEFAULT_JSXGRAPH_FEATURES,
      ...(config.behavior?.features || {}),
    };

    const behavior: ArithmeticSeriesBehaviorConfig = {
      ...DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG,
      ...(config.behavior || {}),
      features,
    };

    if (behavior.transitions) {
      this.instanceTransitions = { ...behavior.transitions };
    }

    this.config = {
      ...config,
      params,
      display,
      behavior,
    };

    this.params = {
      a: this.config.params.a!,
      d: this.config.params.d!,
      numTerms: this.config.params.numTerms!,
    };

    const boundingbox = this.calculateOptimalBoundingBox();

    this.initBoard({
      boundingbox,
      axis: false,
    });

    // Create grid
    this.createGrid(this.config.display.grid);

    // Create baseline
    this.createBaseline();

    // Create sliders if enabled
    if (this.config.behavior?.features?.sliders) {
      this.createSeriesSliders(this.config.behavior.features.sliders);
    }

    // Create visualization elements
    if (this.config.display.bars !== false) {
      this.createBars(this.config.display.bars);
    }

    if (this.config.display.sumLine !== false) {
      this.createSumLine(this.config.display.sumLine);
    }

    if (this.config.display.points !== false) {
      this.createSumPoints(this.config.display.points);
    }

    // Wire event handlers
    if (this.config.behavior?.events) {
      this.wireEvents(this.config.behavior.events);
    }
  }

  private createSeriesSliders(sliderConfig: boolean | SlidersFeatureConfig): void {
    if (sliderConfig === false) {
      return;
    }

    const config =
      typeof sliderConfig === 'boolean'
        ? ({ enabled: true, parameters: ['a', 'd'] } as SlidersFeatureConfig)
        : sliderConfig;

    if (!config.enabled) {
      return;
    }

    const parameters: Array<{ name: string; range: [number, number, number] }> = [];
    const enabledParams = config.parameters || ['a', 'd'];

    if (enabledParams.includes('a')) {
      parameters.push({ name: 'a', range: [-10, this.params.a, 20] });
    }
    if (enabledParams.includes('d')) {
      parameters.push({ name: 'd', range: [-5, this.params.d, 10] });
    }

    const redColor = this.themeService.getColor('pink');
    const skyColor = this.themeService.getColor('sky');

    const defaultIndividualStyles: SlidersFeatureConfig['individualStyles'] = {
      a: {
        strokeColor: redColor,
        highlightStrokeColor: redColor,
        highline: { strokeColor: redColor },
      },
      d: {
        strokeColor: skyColor,
        highlightStrokeColor: skyColor,
        highline: { strokeColor: skyColor },
      },
    };

    const mergedIndividualStyles = Object.fromEntries(
      enabledParams.map((param) => [param, { ...defaultIndividualStyles[param], ...config.individualStyles?.[param] }]),
    );

    this.sliders = this.createSliders(parameters, {
      ...config,
      enabled: true,
      parameters: enabledParams,
      individualStyles: mergedIndividualStyles,
    });
  }

  private renderMath(latex: string): string {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: false,
    });
  }

  private getParam(name: 'a' | 'd' | 'numTerms'): number {
    const slider = this.sliders.get(name);
    return slider ? this.getSliderValue(slider) : this.params[name];
  }

  private getTerm(n: number): number {
    const a = this.getParam('a');
    const d = this.getParam('d');
    return a + (n - 1) * d;
  }

  private getSum(n: number): number {
    const a = this.getParam('a');
    const d = this.getParam('d');
    // Sₙ = n/2 * (2a + (n-1)d)
    return (n / 2) * (2 * a + (n - 1) * d);
  }

  private normalizeFeatureConfig(
    config: boolean | ArithmeticSeriesFeatureConfig | undefined,
  ): ArithmeticSeriesFeatureConfig {
    if (config === undefined || config === false) {
      return { enabled: false };
    }
    if (typeof config === 'boolean') {
      return { enabled: config };
    }
    return { ...config, enabled: config.enabled !== false };
  }

  private createBaseline(): void {
    const bbox = this.board.getBoundingBox();
    const xMin = bbox[0];
    const xMax = bbox[2];

    const theme = this.themeService.getTheme();
    const axisColor = theme.colors.axis;

    const baseline = this.board.create(
      'line',
      [
        [xMin, 0],
        [xMax, 0],
      ],
      {
        strokeColor: axisColor,
        strokeWidth: 1,
        straightFirst: false,
        straightLast: false,
        fixed: true,
        highlight: false,
      },
    );

    this.objects.set('baseline', baseline);
  }

  private createBars(display: boolean | ArithmeticSeriesFeatureConfig | undefined): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS.bars;
    const barColor = this.themeService.getColor(colorName);
    const barShade = this.themeService.getShade(colorName);
    const highlightColor = this.themeService.getColor(DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS.highlight);
    const labelColor = this.themeService.getColor('neutral');
    const barWidth = 0.6;

    const numTerms = this.params.numTerms;

    for (let n = 1; n <= numTerms; n++) {
      const termIndex = n;

      // Create bar as a polygon
      const bar = this.board.create(
        'polygon',
        [
          [() => termIndex - barWidth / 2, 0],
          [() => termIndex + barWidth / 2, 0],
          [() => termIndex + barWidth / 2, () => this.getTerm(termIndex)],
          [() => termIndex - barWidth / 2, () => this.getTerm(termIndex)],
        ],
        {
          fillColor: () => (this.hoveredTerm !== null && termIndex <= this.hoveredTerm ? highlightColor : barShade),
          fillOpacity: () => (this.hoveredTerm !== null && termIndex <= this.hoveredTerm ? 0.7 : 0.4),
          strokeColor: () => (this.hoveredTerm !== null && termIndex <= this.hoveredTerm ? highlightColor : barColor),
          strokeWidth: 2,
          hasInnerPoints: false,
          vertices: { visible: false },
          borders: {
            strokeColor: () => (this.hoveredTerm !== null && termIndex <= this.hoveredTerm ? highlightColor : barColor),
            strokeWidth: 2,
          },
          withLines: false,
          highlight: false,
        },
      );

      this.objects.set(`bar-${n}`, bar as GeometryElement);

      // Add term label below x-axis (just u₁, u₂, etc.)
      const termLabel = this.board.create(
        'text',
        [
          termIndex,
          () => -1.5,
          () => {
            const isHighlighted = this.hoveredTerm !== null && termIndex <= this.hoveredTerm;
            const color = isHighlighted ? highlightColor : labelColor;
            return `<span style="color: ${color}; font-weight: ${isHighlighted ? '700' : '400'}">${this.renderMath(
              `u_{${termIndex}}`,
            )}</span>`;
          },
        ],
        {
          anchorX: 'middle',
          anchorY: 'top',
          fontSize: 12,
          parse: false,
          highlight: false,
        },
      );
      this.objects.set(`term-label-${n}`, termLabel as GeometryElement);
    }
  }

  private createSumLine(display: boolean | ArithmeticSeriesFeatureConfig | undefined): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS.sumLine;
    const lineColor = this.themeService.getColor(colorName);

    const numTerms = this.params.numTerms;

    // Create straight line segments connecting consecutive sum points
    for (let n = 1; n < numTerms; n++) {
      const fromIndex = n;
      const toIndex = n + 1;

      const lineSegment = this.board.create(
        'segment',
        [
          [fromIndex, () => this.getSum(fromIndex)],
          [toIndex, () => this.getSum(toIndex)],
        ],
        {
          strokeColor: lineColor,
          strokeWidth: 2,
          fixed: true,
          highlight: false,
        },
      );
      this.objects.set(`sum-line-${n}`, lineSegment);
    }
  }

  private createSumPoints(display: boolean | ArithmeticSeriesFeatureConfig | undefined): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS.points;
    const pointColor = this.themeService.getColor(colorName);
    const highlightColor = this.themeService.getColor(DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS.highlight);

    const numTerms = this.params.numTerms;

    for (let n = 1; n <= numTerms; n++) {
      const termIndex = n;

      const point = this.board.create('point', [termIndex, () => this.getSum(termIndex)], {
        name: '',
        size: 5,
        fillColor: () => (this.hoveredTerm === termIndex ? highlightColor : pointColor),
        strokeColor: () => (this.hoveredTerm === termIndex ? highlightColor : pointColor),
        fixed: true,
        withLabel: false,
        highlight: true,
        highlightFillColor: highlightColor,
        highlightStrokeColor: highlightColor,
      }) as GeometryElement;

      // Add hover events
      (point as GeometryElement).on('over', () => {
        this.hoveredTerm = termIndex;
        this.showSumTooltip(termIndex);
        this.board.update();
      });

      (point as GeometryElement).on('out', () => {
        this.hoveredTerm = null;
        this.hideSumTooltip();
        this.board.update();
      });

      this.objects.set(`sum-point-${n}`, point as GeometryElement);

      // Add Sₙ = value label above the point (to avoid overlapping the line)
      const sumLabel = this.board.create(
        'text',
        [
          () => termIndex,
          () => this.getSum(termIndex) + 2,
          () => this.renderMath(`S_{${termIndex}} = ${this.getSum(termIndex).toFixed(0)}`),
        ],
        {
          anchorX: 'middle',
          anchorY: 'bottom',
          fontSize: 11,
          parse: false,
          highlight: false,
          cssStyle: () => {
            const isHighlighted = this.hoveredTerm === termIndex;
            return isHighlighted ? `font-weight: 700; color: ${highlightColor};` : `color: ${pointColor};`;
          },
        },
      );
      this.objects.set(`sum-label-${n}`, sumLabel as GeometryElement);
    }
  }

  private showSumTooltip(n: number): void {
    this.hideSumTooltip();

    const highlightColor = this.themeService.getColor(DEFAULT_ARITHMETIC_SERIES_FEATURE_COLORS.highlight);

    // Build the sum expression: S₄ = u₁ + u₂ + u₃ + u₄ = 26
    const terms = Array.from({ length: n }, (_, i) => `u_{${i + 1}}`).join(' + ');
    const sumValue = this.getSum(n);
    const tooltipText = this.renderMath(`S_{${n}} = ${terms} = ${sumValue.toFixed(0)}`);

    const x = n;
    const y = this.getSum(n) + 5;

    this.tooltipElement = this.board.create('text', [x, y, tooltipText], {
      anchorX: 'middle',
      anchorY: 'bottom',
      fontSize: 14,
      parse: false,
      highlight: false,
      cssStyle: `
          background: white;
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid ${highlightColor};
          color: ${highlightColor};
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        `,
    }) as GeometryElement;
  }

  private hideSumTooltip(): void {
    if (this.tooltipElement) {
      this.board.removeObject(this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  private calculateOptimalBoundingBox(): [number, number, number, number] {
    const a = this.getParam('a');
    const d = this.getParam('d');
    const numTerms = this.getParam('numTerms');

    // Calculate max sum
    const maxSum = this.getSum(numTerms);
    const minSum = Math.min(0, this.getSum(1));

    // Calculate term range for bars
    const terms = Array.from({ length: numTerms }, (_, i) => a + i * d);
    const minTerm = Math.min(...terms, 0);

    const xMin = 0;
    const xMax = numTerms + 2; // Extra space for Sₙ labels on right
    const yMin = Math.min(minTerm, minSum) - 5; // Space for term labels
    const yMax = Math.max(maxSum, ...terms) + 10; // Space for tooltip and sum labels

    return [xMin, yMax, xMax, yMin];
  }

  private fitToSeries(): void {
    if (!this.board) {
      return;
    }
    const bbox = this.calculateOptimalBoundingBox();
    this.board.setBoundingBox(bbox, false);
  }

  protected override getRegionBoundingBox(region: string | number): [number, number, number, number] | null {
    if (region === 'all' || region === 'series') {
      return this.calculateOptimalBoundingBox();
    }
    return null;
  }

  protected override async performAction(
    action: ArithmeticSeriesActionType,
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    switch (action) {
      case 'UPDATE_PARAMS':
        return this.actionUpdateParams(payload, transition);
      case 'FIT_TO_SERIES':
        return this.actionFitToSeries(transition);
      case 'HIGHLIGHT_SUM':
        return this.actionHighlightSum(payload);
      default:
        return super.performAction(action, payload, transition);
    }
  }

  private async actionUpdateParams(
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    if (!payload || !('param' in payload) || !('value' in payload)) {
      return { success: false, error: 'Missing param or value in payload' };
    }

    const param = payload['param'] as 'a' | 'd' | 'numTerms';
    const value = payload['value'] as number;

    if (!['a', 'd', 'numTerms'].includes(param)) {
      return { success: false, error: `Invalid param '${param}'` };
    }

    if (!transition || transition.type === 'none' || transition.duration === 0) {
      const slider = this.sliders.get(param);
      if (slider) {
        this.setSliderValue(slider, value);
      } else {
        this.params[param] = value;
      }
      this.board.update();
      this.updateLabels();
      this.fitToSeries();
      return { success: true };
    }

    // Animated update
    const startValue = this.getParam(param);
    const endValue = value;
    const steps = Math.max(1, Math.round(transition.duration / 16));
    const stepDuration = transition.duration / steps;
    const easingFn = this.getEasingFunction(transition.easing);

    for (let step = 1; step <= steps; step++) {
      if (!this.board) {
        return { success: false, error: 'Board destroyed during animation' };
      }

      const progress = easingFn(step / steps);
      const interpolated = startValue + (endValue - startValue) * progress;

      const slider = this.sliders.get(param);
      if (slider) {
        this.setSliderValue(slider, interpolated);
      } else {
        this.params[param] = interpolated;
      }

      this.board.update();
      this.updateLabels();
      this.fitToSeries();

      if (step < steps) {
        await new Promise((resolve) => setTimeout(resolve, stepDuration));
      }
    }
    return { success: true };
  }

  private async actionFitToSeries(transition?: TransitionProfile): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    const bbox = this.calculateOptimalBoundingBox();
    await this.animateBoundingBox(bbox, transition);
    return { success: true };
  }

  private actionHighlightSum(payload?: Record<string, unknown>): ActionResult {
    if (!payload || !('n' in payload)) {
      this.hoveredTerm = null;
      this.hideSumTooltip();
    } else {
      const n = payload['n'] as number;
      this.hoveredTerm = n;
      this.showSumTooltip(n);
    }
    this.board?.update();
    return { success: true };
  }
}
